import React, {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useEffect,
  useState
} from 'react';

import AuthService from '../../shared/services/AuthService';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Grid from '@mui/material/Unstable_Grid2';
import ProgressMessageBox from './ProgressMessageBox';
import { Link as RouterLink } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useRequestPasswordReset } from '../hooks/useRequestPasswordReset';

interface RequestPasswordResetProps {
  onEmailSent: (
    success: boolean,
    email: string,
    linkedAccount: boolean,
    parentFullName?: string
  ) => void;
}

const RequestPasswordReset = (props: RequestPasswordResetProps) => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [canRequest, setCanRequest] = useState<boolean>();
  const [helpText, setHelpText] = useState<string>('');
  const [requestStatus, setRequestStatus] = useState<boolean>(false);
  const locationOrigin = window.location.origin;
  const tenant = AuthService.Tenant;

  const {
    canResetPassword,
    checkingUser,
    canResetPasswordData,
    requestResetPassword,
    requestResetPasswordLoading,
    requestResetPasswordData,
    requestResetPasswordError,
    requestLinkedAccountResetPassword,
    requestLinkedAccountResetPasswordLoading,
    requestLinkedAccountResetPasswordData,
    requestLinkedAccountResetPasswordError
  } = useRequestPasswordReset();

  useEffect(() => {
    if (canRequest) {
      doRequestResetPassword();
    }
  }, [canRequest]);

  const email = emailOrUsername.trim();

  useEffect(() => {
    if (requestStatus && !checkingUser) {
      const requestData = canResetPasswordData?.canResetPassword;
      if (!requestData) {
        setHelpText(`The account doesn't exist.`);
        return;
      }
      if (requestData.eligible) {
        setCanRequest(requestData.eligible);
      }
      if (!requestData.user.verifiedemail && !requestData.parent?.email) {
        setHelpText(`The account isn't verified or linked.`);
      }
    }
  }, [canResetPasswordData]);

  useEffect(() => {
    if (requestResetPasswordError) {
      props.onEmailSent(false, email, false);
    }
    if (requestLinkedAccountResetPasswordError) {
      props.onEmailSent(false, email, true);
    }
  }, [requestResetPasswordError, requestLinkedAccountResetPasswordError]);

  useEffect(() => {
    if (requestResetPasswordData?.requestResetPassword) {
      props.onEmailSent(
        requestResetPasswordData.requestResetPassword,
        email,
        false
      );
    }
  }, [requestResetPasswordData]);

  useEffect(() => {
    if (
      requestLinkedAccountResetPasswordData?.requestLinkedAccountResetPassword
    ) {
      props.onEmailSent(
        requestLinkedAccountResetPasswordData.requestLinkedAccountResetPassword,
        canResetPasswordData?.canResetPassword?.parent?.email,
        true,
        `${canResetPasswordData?.canResetPassword?.parent?.firstname || ''} ${
          canResetPasswordData?.canResetPassword?.parent?.lastname || ''
        }`
      );
    }
  }, [requestLinkedAccountResetPasswordData]);

  if (requestResetPasswordLoading || requestLinkedAccountResetPasswordLoading) {
    const message = requestResetPasswordLoading
      ? 'Sending an email with a link to reset your password...'
      : 'Requesting password reset...';
    return <ProgressMessageBox message={message} />;
  }

  const handleEmailOrUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailOrUsername(e.target.value);
    if (requestStatus) {
      setHelpText('');
      setRequestStatus(false);
    }
  };

  const doRequestResetPassword = () => {
    canResetPasswordData?.canResetPassword?.parent
      ? requestLinkedAccountResetPassword(
          tenant,
          canResetPasswordData.canResetPassword.user,
          canResetPasswordData.canResetPassword.parent
        )
      : requestResetPassword(locationOrigin, tenant, email);
  };

  const handleSubmit = (
    event: MouseEvent<HTMLButtonElement> | FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setCanRequest(undefined);
    canResetPassword(tenant, email);
    setRequestStatus(true);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        container
        justifyContent="center"
        className="login-body"
        direction="column"
        spacing={2}
      >
        <Grid>
          <Typography variant="h5" color="primary" align="center">
            Need a new password?
          </Typography>
        </Grid>
        <Grid>
          <Typography
            variant="body2"
            align="center"
            className="request-password-reset-message"
          >
            No worries! Just use the form below to request one.
          </Typography>
          <Typography
            variant="body2"
            align="center"
            className="request-password-reset-message"
          >
            We will send you an email with a link to reset your password.
          </Typography>
          <Typography
            variant="body2"
            align="center"
            className="request-password-reset-message"
          >
            Keep an eye on your junk folder.
          </Typography>
        </Grid>
        <Grid>
          <Grid container alignItems="center" spacing={1}>
            <Grid style={{ flex: 1 }}>
              <TextField
                name="Email"
                label="Email or username"
                variant="outlined"
                fullWidth={true}
                margin="dense"
                size="small"
                value={emailOrUsername}
                error={!!helpText}
                helperText={helpText}
                onChange={handleEmailOrUsernameChange}
                autoFocus
              />
            </Grid>
            <Grid>
              {checkingUser && <CustomLoadingProgress size={20} />}
              {canRequest && (
                <CheckIcon
                  style={{ color: 'green', verticalAlign: 'middle' }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Button
            variant="contained"
            size="large"
            color="primary"
            name="button"
            style={{ width: '100%' }}
            disabled={!!!email}
            onClick={handleSubmit}
          >
            Request Password Reset
          </Button>
        </Grid>
        <Grid container justifyContent="center">
          <RouterLink to="/" className="back-to-login-link">
            Back to Login
          </RouterLink>
        </Grid>
      </Grid>
    </form>
  );
};

export default RequestPasswordReset;
